import * as React from "react";

import { cn } from "@/lib/utils";

// SECURITY: Stable per-mount random suffix used when a field has NOT opted
// into autofill. Browsers pattern-match on `name`/`id` values like "email",
// "phone", "name", "contact" and inject saved credentials. Appending an
// unguessable suffix breaks that pattern matching across browsers.
function useAutofillSafeAttrs(
  autoComplete: string | undefined,
  name: string | undefined,
  id: string | undefined,
) {
  const suffix = React.useMemo(
    () => Math.random().toString(36).slice(2, 8),
    [],
  );
  // If the caller explicitly opted into a real autocomplete value
  // (e.g. "email", "current-password", "new-password", "name", "tel"),
  // do NOT mangle name/id: the browser must be able to autofill normally.
  const isAutofillOptIn =
    autoComplete && autoComplete !== "off" && autoComplete !== "new-password";
  if (isAutofillOptIn) {
    return { safeName: name, safeId: id };
  }
  return {
    safeName: name ? `${name}_${suffix}` : `field_${suffix}`,
    safeId: id ? `${id}_${suffix}` : undefined,
  };
}

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, autoComplete, name, id, onFocus, readOnly, ...props }, ref) => {
    // SECURITY: Multi-layer browser autofill defence applied to EVERY input
    // by default. Pages that legitimately need autofill (Login, Signup,
    // Reset Password, profile name/email/tel) opt back in by passing an
    // explicit `autoComplete="email" | "name" | "tel" | "current-password"
    // | "new-password"` etc.
    //
    // Layers:
    //  1. autoComplete defaults to "off"
    //  2. data-form-type="other": hint to Edge's autofill engine
    //  3. data-lpignore / data-1p-ignore: LastPass + 1Password opt-out
    //  4. Randomised name/id suffix: defeats heuristic field matching
    //  5. readonly-on-mount, removed on focus: defeats Chrome/Edge ignoring
    //     autocomplete="off" on first paint. Only applied when no explicit
    //     autocomplete is set, so login fields still work.
    const effectiveAutoComplete = autoComplete ?? "off";
    const isAutofillOptIn =
      autoComplete && autoComplete !== "off" && autoComplete !== "new-password";

    const { safeName, safeId } = useAutofillSafeAttrs(autoComplete, name, id);

    const [autofillBlocked, setAutofillBlocked] = React.useState(
      !isAutofillOptIn && readOnly !== true,
    );

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (autofillBlocked) setAutofillBlocked(false);
      onFocus?.(e);
    };

    return (
      <input
        type={type}
        name={safeName}
        id={safeId}
        autoComplete={effectiveAutoComplete}
        data-form-type={isAutofillOptIn ? undefined : "other"}
        data-lpignore={isAutofillOptIn ? undefined : "true"}
        data-1p-ignore={isAutofillOptIn ? undefined : "true"}
        readOnly={readOnly || autofillBlocked}
        onFocus={handleFocus}
        className={cn(
          "flex h-10 w-full rounded-lg border border-[rgba(13,31,20,0.16)] bg-white px-3.5 py-2.5 text-sm font-body text-nursly-ink ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-nursly-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nursly-sage focus-visible:ring-offset-0 focus-visible:border-nursly-sage disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
